// Overview tab of the customer view (CRM > customer): how they're ordering,
// what they usually order (with Draft reorder), unpaid Zoho invoices, notes,
// and the latest orders with their items.

import { useEffect, useState } from 'react'
import { AlertTriangle, Check, ClipboardCopy, FileText, Loader2, Package, Pencil, Sparkles, StickyNote } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Badge } from '../../lib/shadcn/badge'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { useGenerateEmailContent, useGetCustomerInvoices, useUpdateCustomerNotes } from '../../hooks/backend/crm'
import { formatDate } from '../../utils/format'
import type { MessageTarget } from './MessageDialog'
import { HelpText } from '../../components/HelpText'

export interface OrderPace {
  usual_gap_days: number | null
  restocks: number
  last_order_date: string | null
  days_since_last_order: number | null
}

export interface UsualItem {
  item_name: string
  item_sku: string
  times_ordered: number
  window_orders: number
  usual_quantity: number
  last_quantity: number
  last_ordered: string | null
}

export interface OrderWithItems {
  salesorder_number: string
  order_date: string | null
  status: string
  total: number
  items?: Array<{ item_name: string; item_sku: string; quantity: number }>
}

export interface CustomerNotes {
  notes_enabled: boolean
  notes: string
  lead_notes: Array<{ lead_id: number; name: string; notes: string }>
}

interface CustomerInvoice {
  invoice_id: string
  invoice_number: string
  date: string | null
  due_date: string | null
  status: string
  total: number
  balance: number
  days_overdue: number
}

interface Props {
  customerId: string
  name: string
  firstName: string
  email: string
  phone: string
  pace: OrderPace | null
  usualItems: UsualItem[]
  orders: OrderWithItems[]
  notes: CustomerNotes | null
  onMessage: (target: MessageTarget) => void
  onNotesSaved: (notes: string) => void
  onShowAllOrders: () => void
}

const money = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0)

export function formatDays(days: number): string {
  if (days >= 14 && Math.abs(days / 7 - Math.round(days / 7)) <= 0.25) return `${Math.round(days / 7)} weeks`
  if (days === 1) return '1 day'
  return `${days} days`
}

/** Usual items formatted for the email prompt and the copy button: "12 x Fuego - Small". */
export function itemLines(items: UsualItem[]): string[] {
  return items.map(i => `${i.usual_quantity} x ${i.item_name}`)
}

function buildReorderPrompt(name: string, pace: OrderPace | null, items: UsualItem[]): string {
  const list = itemLines(items).join('; ')
  const last = pace?.last_order_date ? formatDate(pace.last_order_date) : null
  return [
    `A friendly note to ${name}, a wholesale store that carries our products, offering to put together their next order.`,
    last ? `Their last order was on ${last}.` : '',
    pace?.usual_gap_days ? `They usually restock about every ${formatDays(pace.usual_gap_days)}.` : '',
    list
      ? `Include their usual order as a simple list they can reply to with changes, exactly these items and quantities: ${list}.`
      : '',
    'Ask them to reply with any changes, or just "yes" to place it as is.',
    "Keep it short and personal. Don't mention that we track their ordering or quote day counts.",
  ]
    .filter(Boolean)
    .join(' ')
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string | undefined; tone?: 'warn' | 'bad' | undefined }) {
  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-3',
        tone === 'bad' ? 'border-destructive/50' : tone === 'warn' ? 'border-yellow-500/50' : 'border-border'
      )}
    >
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div
        className={cn(
          'text-xl font-bold tabular-nums',
          tone === 'bad' ? 'text-destructive' : tone === 'warn' ? 'text-yellow-600 dark:text-yellow-400' : 'text-foreground'
        )}
      >
        {value}
      </div>
      {sub ? <div className="text-xs text-muted-foreground mt-0.5">{sub}</div> : null}
    </div>
  )
}

export default function CustomerOverview({
  customerId,
  name,
  firstName,
  email,
  phone,
  pace,
  usualItems,
  orders,
  notes,
  onMessage,
  onNotesSaved,
  onShowAllOrders,
}: Props) {
  const generate = useGenerateEmailContent()
  const getInvoices = useGetCustomerInvoices()
  const saveNotes = useUpdateCustomerNotes()
  const [drafting, setDrafting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [editingNotes, setEditingNotes] = useState(false)
  const [notesDraft, setNotesDraft] = useState(notes?.notes ?? '')

  useEffect(() => {
    getInvoices.trigger({ customer_id: customerId }, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  useEffect(() => {
    if (!editingNotes) setNotesDraft(notes?.notes ?? '')
  }, [notes?.notes, editingNotes])

  const invoiceData = getInvoices.data as { invoices: CustomerInvoice[]; balance: number; error: string | null } | undefined
  const invoices = invoiceData?.invoices ?? []
  const overdueInvoices = invoices.filter(i => i.days_overdue > 0)

  const since = pace?.days_since_last_order ?? null
  const gap = pace?.usual_gap_days ?? null
  const dueIn = since !== null && gap !== null ? gap - since : null
  const sinceTone: 'warn' | 'bad' | undefined =
    since === null || gap === null ? undefined : since >= Math.max(gap * 4, 60) ? 'bad' : since >= Math.max(gap * 1.5, gap + 7) ? 'warn' : undefined

  const handleDraft = async () => {
    setDrafting(true)
    try {
      const first = firstName.trim()
      const draft = (await generate.trigger({
        prompt: buildReorderPrompt(name, pace, usualItems),
        tone: 'Friendly',
        recipient_name: first && first.toLowerCase() !== name.toLowerCase() ? `${first} at ${name}` : name,
      })) as { subject: string; body: string }
      onMessage({ customerId, name, email, phone, draft })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to draft the reorder email')
    } finally {
      setDrafting(false)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(itemLines(usualItems).join('\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error("Couldn't copy to the clipboard")
    }
  }

  const handleSaveNotes = async () => {
    try {
      await saveNotes.trigger({ customer_id: customerId, notes: notesDraft })
      onNotesSaved(notesDraft)
      setEditingNotes(false)
      toast.success('Notes saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save notes')
    }
  }

  const latest = orders.slice(0, 3)

  return (
    <div className="space-y-5">
      {/* Ordering at a glance */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile
          label="Last order"
          value={since === null ? '—' : since === 0 ? 'Today' : `${formatDays(since)} ago`}
          sub={pace?.last_order_date ? formatDate(pace.last_order_date) : 'No orders yet'}
          tone={sinceTone}
        />
        <Tile
          label="Usually orders"
          value={gap === null ? '—' : `Every ${formatDays(gap)}`}
          sub={gap === null ? (pace && pace.restocks > 0 ? `${pace.restocks} restock${pace.restocks === 1 ? '' : 's'} so far` : undefined) : `${pace?.restocks ?? 0} restocks`}
        />
        <Tile
          label="Next order"
          value={dueIn === null ? '—' : dueIn > 0 ? `In ${formatDays(dueIn)}` : dueIn === 0 ? 'Due today' : `${formatDays(-dueIn)} late`}
          sub={dueIn === null ? 'Needs 3+ restocks' : 'Based on their usual gap'}
          tone={dueIn !== null && dueIn < 0 ? sinceTone ?? 'warn' : undefined}
        />
        <Tile
          label="Unpaid"
          value={getInvoices.loading && !invoiceData ? '…' : invoiceData?.error ? '—' : money(invoiceData?.balance ?? 0)}
          sub={
            invoiceData?.error
              ? 'Zoho not reachable'
              : invoices.length === 0
                ? 'No open invoices'
                : `${invoices.length} invoice${invoices.length === 1 ? '' : 's'}${overdueInvoices.length ? `, ${overdueInvoices.length} overdue` : ''}`
          }
          tone={overdueInvoices.length > 0 ? 'bad' : undefined}
        />
      </div>

      {/* Usual order */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Package className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Usual order</h2>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleCopy} disabled={usualItems.length === 0}>
              {copied ? <Check className="w-4 h-4" /> : <ClipboardCopy className="w-4 h-4" />}
              Copy list
            </Button>
            <Button size="sm" onClick={handleDraft} disabled={drafting || usualItems.length === 0}>
              {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Draft reorder
            </Button>
          </div>
        </div>
        {usualItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">No order items yet.</p>
        ) : (
          <>
            <ul className="divide-y divide-border text-sm">
              {usualItems.map(i => (
                <li key={`${i.item_name}|${i.item_sku}`} className="flex items-center gap-3 py-1.5">
                  <span className="w-10 text-right font-semibold tabular-nums text-foreground">{i.usual_quantity}</span>
                  <span className="flex-1 min-w-0 text-foreground break-words">{i.item_name}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {i.times_ordered} of {i.window_orders} order{i.window_orders === 1 ? '' : 's'}
                    {i.last_quantity !== i.usual_quantity ? ` · last ${i.last_quantity}` : ''}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground mt-2"><HelpText>
              Quantity is what they usually order of each item, from the last 6 months. Draft reorder writes an email
              in your voice with this list for you to review before sending.
            </HelpText></p>
          </>
        )}
      </div>

      {/* Unpaid invoices */}
      {(invoices.length > 0 || invoiceData?.error) && (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">Unpaid invoices</h2>
          </div>
          {invoiceData?.error ? (
            <p className="text-sm text-muted-foreground flex items-start gap-1.5">
              <AlertTriangle className="w-4 h-4 text-yellow-600 shrink-0 mt-0.5" />
              Couldn&apos;t load invoices from Zoho Books: {invoiceData.error}
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {invoices.map(inv => (
                <li key={inv.invoice_id || inv.invoice_number} className="flex items-center gap-3 py-1.5 flex-wrap">
                  <span className="font-medium text-foreground">{inv.invoice_number}</span>
                  <span className="text-muted-foreground">{formatDate(inv.date)}</span>
                  {inv.days_overdue > 0 ? (
                    <Badge variant="destructive">{formatDays(inv.days_overdue)} overdue</Badge>
                  ) : inv.due_date ? (
                    <span className="text-xs text-muted-foreground">due {formatDate(inv.due_date)}</span>
                  ) : null}
                  <span className="ml-auto tabular-nums font-semibold text-foreground">{money(inv.balance)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Notes */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 mb-2">
          <StickyNote className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Notes</h2>
          {notes?.notes_enabled && !editingNotes && (
            <Button variant="outline" size="sm" className="ml-auto h-7 px-2" onClick={() => setEditingNotes(true)}>
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          )}
        </div>
        {!notes?.notes_enabled ? (
          <p className="text-sm text-muted-foreground">
            Notes need a one-time database update (migrations/3.71.0-customer-notes.sql).
          </p>
        ) : editingNotes ? (
          <div className="space-y-2">
            <Textarea
              value={notesDraft}
              onChange={e => setNotesDraft(e.target.value)}
              rows={5}
              placeholder="Delivery instructions, who to ask for, what sells well there…"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditingNotes(false)} disabled={saveNotes.loading}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveNotes} disabled={saveNotes.loading}>
                {saveNotes.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save
              </Button>
            </div>
          </div>
        ) : notes.notes.trim() ? (
          <p className="text-sm text-foreground whitespace-pre-wrap">{notes.notes}</p>
        ) : (
          <p className="text-sm text-muted-foreground">No notes yet.</p>
        )}
        {notes && notes.lead_notes.length > 0 && (
          <div className="mt-3 pt-2 border-t border-border space-y-1">
            <div className="text-xs text-muted-foreground">From the lead</div>
            {notes.lead_notes.map(l => (
              <p key={l.lead_id} className="text-sm text-foreground whitespace-pre-wrap">{l.notes}</p>
            ))}
          </div>
        )}
      </div>

      {/* Latest orders with items */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-sm font-semibold text-foreground">Latest orders</h2>
          {orders.length > latest.length && (
            <button className="ml-auto text-xs text-primary hover:underline" onClick={onShowAllOrders}>
              All orders
            </button>
          )}
        </div>
        {latest.length === 0 ? (
          <p className="text-sm text-muted-foreground">No orders yet.</p>
        ) : (
          <div className="space-y-3">
            {latest.map(o => (
              <div key={o.salesorder_number} className="text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-foreground">{o.salesorder_number}</span>
                  <span className="text-muted-foreground">{formatDate(o.order_date)}</span>
                  <Badge variant="secondary" className="capitalize">{o.status || '—'}</Badge>
                  <span className="ml-auto tabular-nums text-foreground">{money(o.total)}</span>
                </div>
                {o.items && o.items.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {o.items.map(i => `${i.quantity} ${i.item_name}`).join(' · ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
